import {
    Typography,
    DialogContent,
    Button,
    Box,
    Checkbox,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import { useErrorDisplay } from "../components/ErrorDisplay"
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { Proficiency, ProficiencyType } from "../models/dtos";
import ProficiencyClient from "../apiClients/ProficiencyClient";
import { useSearchBox } from "../components/SearchBox";
import { I18N } from "../I18N";
import I18NText from "../components/I18NText";
import { GetUpsertIcon } from "../Helpers";
import { UpsertAction } from "../Enums";
import MobileEntityCard from "../components/responsive/MobileEntityCard";
import ResponsiveCollection from "../components/responsive/ResponsiveCollection";
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";
import useViewportMode from "../hooks/useViewportMode";

export interface ChooseProficienciesPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    onAdd?: (ids: string[]) => Promise<void> | void;
    icon?: JSX.Element;
    proficiencies?: Proficiency[]
    excludeIds?: string[]
}

const ChooseProficienciesPopup = ({ proficiencies = [], excludeIds = [], onAdd = async () => { }, icon = GetUpsertIcon(UpsertAction.Create), ...btnProps }: ChooseProficienciesPopupProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>();
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const [SearchBox, searchFilter] = useSearchBox();
    const viewport = useViewportMode();

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [data, setData] = useState<Proficiency[]>(proficiencies);

    const title = `Select Proficiencies`;
    const disableSave = selectedIds.length < 1;
    const loadData = async () => {
        try {
            setBusy(true);
            if (data.length === 0)
                setData(await new ProficiencyClient().GetAll());
                
        } catch (error) {
            setError(error, "Failed to load the proficencies")
        } finally {
            setBusy(false);
        }
    }

    const handleSubmit = async () => {
        try {
            setBusy(true);
            setOpen(false);
            await onAdd(selectedIds);
        } catch (error) {
            setError(error, "Failed to Add proficencies");
        }
        finally {
            setBusy(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        setSelectedIds([]);
        await loadData();
    };

    const handleRowClicked = (ids: string[]) => {
        setSelectedIds(ids);
    }

    const dispProficiencies = data.filter(x => !excludeIds.includes(x.TemplateRevisionId))
    const visibleProficiencies = searchFilter(dispProficiencies) as Proficiency[];

    const toggleSelection = (id: string) => setSelectedIds(current => current.includes(id)
        ? current.filter(selectedId => selectedId !== id)
        : [...current, id]);

    const renderProficiencyCard = (proficiency: Proficiency) => (
        <MobileEntityCard
            ariaLabel={`Selectable proficiency: ${proficiency.Name}`}
            title={proficiency.Name}
            subtitle={I18N.Translate(ProficiencyType[proficiency.Type] ?? String(proficiency.Type))}
            status={<Checkbox
                checked={selectedIds.includes(proficiency.Id)}
                onChange={() => toggleSelection(proficiency.Id)}
                inputProps={{ 'aria-label': `Select proficiency: ${proficiency.Name}` }}
                sx={{ width: 44, height: 44 }}
            />}
        />
    );

    const desktopGrid = (
        <div style={{ height: 500 }}>
            <DataGridPro
                density="compact"
                onSelectionModelChange={handleRowClicked}
                checkboxSelection={true}
                components={{ Toolbar: GridToolbar }}
                columns={[
                    { field: 'Type', headerName: I18N.Translate('Type'), flex: 1 },
                    { field: 'Name', headerName: I18N.Translate('Name'), flex: 4, editable: true }
                ]}
                rows={visibleProficiencies.map(x => (
                    {
                        key: x.Id,
                        id: x.Id,
                        Name: x.Name,
                        Type: x.Type
                    }
                ))}
            />
        </div>
    );

    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title} onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog
            aria-labelledby="customized-dialog-title"
            open={open}
            fullWidth
            maxWidth="md"
            tabletFullScreen="portrait"
            PaperProps={{ sx: { '& > .responsive-dialog-busy': { display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' } } }}
        >
            <BusySpinner busy={busy} className="responsive-dialog-busy">
                <ResponsiveDialogHeader
                    id="customized-dialog-title"
                    title={title}
                    actions={<Box sx={{ minWidth: { xs: 140, sm: 220 } }}><SearchBox /></Box>}
                    onClose={() => setOpen(false)}
                />
                <DialogContent dividers sx={{ p: viewport.isDesktop ? theme.spacing(2) : 0, minHeight: 0, overflowY: 'auto' }}>
                    <ResponsiveCollection
                        viewMode={viewport.mode}
                        items={visibleProficiencies}
                        getKey={proficiency => proficiency.Id}
                        renderCard={renderProficiencyCard}
                        desktop={desktopGrid}
                        ariaLabel="Available proficiencies"
                        emptyState={<Typography sx={{ p: 2 }} color="text.secondary">No proficiencies found</Typography>}
                    />
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Button onClick={() => setOpen(false)} variant='text' color='inherit'><I18NText>Cancel</I18NText></Button>
                    <Button disabled={disableSave} onClick={handleSubmit} variant='outlined' color='primary'><I18NText>Submit</I18NText></Button>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
}

export default ChooseProficienciesPopup;
